import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CloudDownload, Save, Send } from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/common/PageHeader';
import { Panel } from '../../components/common/Panel';
import { NativeSelect } from '../../components/common/NativeSelect';
import { usePrototype } from '../../contexts/PrototypeContext';
import { trafficDivisions } from '../../data/cngs';
interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  mono?: boolean;
  error?: string;
}
function Field({
  id,
  label,
  value,
  onChange,
  required,
  mono,
  error
}: FieldProps) {
  return <div data-id="element-1207" data-mp="2y4cbcap5g2u">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-slate-600" data-id="element-1208" data-mp="2xma7z1cs7n0">
        {label} {required && <span className="text-red-600" data-id="element-1209" data-mp="3u5gv1vr8a1kv">*</span>}
      </label>
      <Input id={id} value={value} onChange={e => onChange(e.target.value)} className={`${mono ? 'font-mono' : ''} ${error ? 'border-red-400' : ''}`} aria-invalid={Boolean(error)} data-id="element-1210" data-mp="2tcic4jxdmook" />
      {error && <p className="mt-1 text-[11px] text-red-600" data-id="element-1211" data-mp="339d7itycs8ym">{error}</p>}
    </div>;
}
export function CngForm() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    getCng,
    setStage
  } = usePrototype();
  const existing = id ? getCng(id) : undefined;
  const editing = Boolean(existing);
  const [f, setF] = useState({
    reg: existing?.reg ?? 'ঢাকা-থ-১৬-০৪৭২',
    chassis: existing?.chassis ?? 'MDH12F45ABP047218',
    engine: existing?.engine ?? '',
    model: existing?.model ?? 'বাজাজ RE 4S (CNG)',
    color: existing?.color ?? 'সবুজ',
    regDate: existing?.brtaRegDate ?? '১৪ ফেব্রুয়ারি ২০২৪',
    fitnessExpiry: existing?.fitnessExpiry ?? '১৩ ফেব্রুয়ারি ২০২৮',
    ownerName: existing?.owner.name ?? 'মোঃ আব্দুল আউয়াল',
    nid: existing?.owner.nid ?? '1983456712390',
    mobile: existing?.owner.mobile ?? '01718-654321',
    address: existing?.owner.address ?? 'বাড়ি ১৮, রোড ২, কল্যাণপুর, মিরপুর, ঢাকা-১২০৭'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof f) => (v: string) => setF(s => ({
    ...s,
    [k]: v
  }));
  const submit = () => {
    const e: Record<string, string> = {};
    if (!f.reg) e.reg = 'রেজিস্ট্রেশন নং আবশ্যক';
    if (!f.engine) e.engine = 'ইঞ্জিন নং আবশ্যক';
    if (!/^\d{10,17}$/.test(f.nid)) e.nid = 'সঠিক NID প্রদান করুন (১০/১৩/১৭ সংখ্যা)';
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error('কিছু আবশ্যক তথ্য অসম্পূর্ণ');
      return;
    }
    if (existing) setStage(existing.id, Math.max(existing.stage, 1));
    toast.success(`${f.reg} যাচাইয়ের জন্য পাঠানো হয়েছে`);
    navigate(existing ? `/admin/cng/${existing.id}` : '/admin/cng');
  };
  return <div data-id="element-1212" data-mp="u771ukqm27b4">
      <PageHeader title={editing ? `সিএনজি তথ্য সম্পাদনা — ${existing?.reg}` : 'নতুন সিএনজি নিবন্ধন'} subtitle="তারকা (*) চিহ্নিত ঘর আবশ্যক। BRTA থেকে তথ্য আনলে স্বয়ংক্রিয়ভাবে পূরণ হবে।" actions={<Button variant="outline" onClick={() => {
      setF(s => ({
        ...s,
        engine: s.engine || 'ENG445566778'
      }));
      toast.info('BRTA রেকর্ড থেকে তথ্য পূরণ করা হয়েছে');
    }} data-id="element-1214" data-mp="o329rd0sj6n0"><CloudDownload className="h-4 w-4" data-id="element-1215" data-mp="fbs8t0nfw88a" />BRTA থেকে তথ্য আনুন</Button>} data-id="element-1213" data-mp="u7gsmajhcihv" />
      <div className="grid gap-4 xl:grid-cols-[1fr_300px]" data-id="element-1216" data-mp="f9p7vlkr0uos">
        <div className="space-y-4" data-id="element-1217" data-mp="dzq5zmlj3pv">
          <Panel title="মৌলিক তথ্য" subtitle="Basic Information" data-id="element-1218" data-mp="h44hptrzfu9">
            <div className="grid gap-4 md:grid-cols-3" data-id="element-1219" data-mp="5ffdjyc8xmbf">
              <Field id="f-reg" label="রেজিস্ট্রেশন নং" value={f.reg} onChange={set('reg')} required error={errors.reg} data-id="element-1220" data-mp="3nk0i4a8ioxy4" />
              <NativeSelect id="f-type" label="যানের ধরন" options={['সিএনজি অটোরিকশা']} data-id="element-1221" data-mp="3sjlgaifv529s" />
              <Field id="f-ch" label="চেসিস নং" value={f.chassis} onChange={set('chassis')} required mono data-id="element-1222" data-mp="1lrui5hgydozr" />
              <Field id="f-en" label="ইঞ্জিন নং" value={f.engine} onChange={set('engine')} required mono error={errors.engine} data-id="element-1223" data-mp="1qq5sdtb5u1ii" />
              <Field id="f-model" label="মডেল" value={f.model} onChange={set('model')} data-id="element-1224" data-mp="3mtk7uqzr8evl" />
              <Field id="f-color" label="রং" value={f.color} onChange={set('color')} data-id="element-1225" data-mp="3hvs4n0tnt6c3" />
            </div>
          </Panel>
          <Panel title="BRTA তথ্য" subtitle="BRTA Information" data-id="element-1226" data-mp="3cy8rk48p6zlq">
            <div className="grid gap-4 md:grid-cols-4" data-id="element-1227" data-mp="38tsmg56wrw7k">
              <Field id="f-rd" label="নিবন্ধনের তারিখ" value={f.regDate} onChange={set('regDate')} required data-id="element-1228" data-mp="33v7mm6vkjejn" />
              <Field id="f-fit" label="ফিটনেস মেয়াদ" value={f.fitnessExpiry} onChange={set('fitnessExpiry')} required data-id="element-1229" data-mp="2z494r2uxm8ea" />
              <NativeSelect id="f-tax" label="ট্যাক্স টোকেন" options={['বৈধ', 'মেয়াদোত্তীর্ণ']} data-id="element-1230" data-mp="2u6ppbzmhzpkr" />
              <NativeSelect id="f-bs" label="BRTA স্ট্যাটাস" options={['যাচাইকৃত', 'অপেক্ষমাণ', 'অমিল']} data-id="element-1231" data-mp="2p7miqaqvdbao" />
            </div>
          </Panel>
          <Panel title="মালিকের তথ্য" subtitle="Owner Information" data-id="element-1232" data-mp="2l72ll76aq7oj">
            <div className="grid gap-4 md:grid-cols-3" data-id="element-1233" data-mp="2g77w98265q5l">
              <Field id="f-on" label="নাম" value={f.ownerName} onChange={set('ownerName')} required data-id="element-1234" data-mp="vo4ex2vs6ixd" />
              <Field id="f-nid" label="NID" value={f.nid} onChange={set('nid')} required mono error={errors.nid} data-id="element-1235" data-mp="10mp6kdwyvua7" />
              <Field id="f-mob" label="মোবাইল" value={f.mobile} onChange={set('mobile')} required mono data-id="element-1236" data-mp="14oaowes9t0rs" />
              <div className="md:col-span-2" data-id="element-1237" data-mp="19nvmolvpf32x"><Field id="f-addr" label="ঠিকানা" value={f.address} onChange={set('address')} data-id="element-1238" data-mp="19mudkmy90qnu" /></div>
              <NativeSelect id="f-div" label="ট্রাফিক বিভাগ" options={trafficDivisions} defaultValue={existing?.division ?? 'মিরপুর'} data-id="element-1239" data-mp="cz7l104apsfo" />
            </div>
          </Panel>
          <div className="flex justify-end gap-2 rounded-lg border border-border bg-white p-4" data-id="element-1240" data-mp="lx0146hud444">
            <Button variant="outline" onClick={() => toast.success('খসড়া সংরক্ষণ করা হয়েছে')} data-id="element-1241" data-mp="qpt6mwl4wgh9"><Save className="h-4 w-4" data-id="element-1242" data-mp="3pfb8ouy5o0d6" />খসড়া সংরক্ষণ</Button>
            <Button onClick={submit} data-id="element-1243" data-mp="3pcozyrzj3etd"><Send className="h-4 w-4" data-id="element-1244" data-mp="3udl2b148eswo" />যাচাইয়ের জন্য পাঠান</Button>
          </div>
        </div>
        <aside className="space-y-4" data-id="element-1245" data-mp="11j4ei3nh2o2m">
          <Panel title="ডকুমেন্ট আপলোড" data-id="element-1246" data-mp="11i32nwymtxju">
            <ul className="space-y-2 text-sm" data-id="element-1247" data-mp="11h1dypop1cjn">
              {[['রেজিস্ট্রেশন সনদ', true], ['ফিটনেস সনদ', true], ['ট্যাক্স টোকেন', true], ['মালিকের NID', false], ['গাড়ির ছবি', false]].map(([d, ok]) => <li key={d as string} className="flex items-center justify-between rounded border border-border px-3 py-2" data-id="element-1248" data-mp="16a2x59vxny91">
                  <span data-id="element-1249" data-mp="ik07t1jel6e4">{d}</span>
                  {ok ? <span className="text-xs font-semibold text-emerald-700" data-id="element-1250" data-mp="il1slxq81791">✓ সংযুক্ত</span> : <button className="text-xs font-semibold text-navy-600" data-id="element-1251" data-mp="dmgsrzevspl4">আপলোড</button>}
                </li>)}
            </ul>
          </Panel>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-xs leading-relaxed text-blue-800" data-id="element-1252" data-mp="mm3kftht6h0z">
            জমা দেওয়ার পর রেকর্ডটি <b data-id="element-1253" data-mp="22zbzankonrc7">তথ্য যাচাই</b> ধাপে যাবে। যাচাইকারী BRTA তথ্যের সাথে মিলিয়ে অনুমোদনের জন্য পাঠাবেন।
          </div>
        </aside>
      </div>
    </div>;
}